import { BIBLE_BOOKS, type BibleBook, type VerseReference } from './bible-utils';
import { getLocalStorage, setLocalStorage } from './storage';

const SCROLL_POSITION_KEY = 'bible-reading-scroll-position';

/**
 * Update the URL with the current reading position (verse-based)
 */
export function updateReadingUrl(book: BibleBook, chapter: number, verse: number): void {
    const url = `/app/read/${book}/${chapter}?verse=${verse}`;
    window.history.replaceState({}, '', url);
}

/**
 * Save scroll position to localStorage (for fallback when returning to app)
 * Format: "John:3:16"
 */
export function saveScrollPosition(book: BibleBook, chapter: number, verse: number): void {
    const position = `${book}:${chapter}:${verse}`;
    setLocalStorage(SCROLL_POSITION_KEY, position);
}

/**
 * Load scroll position from localStorage
 * Returns null if no saved position or invalid format
 */
export function loadScrollPosition(): VerseReference | null {
    const saved = getLocalStorage(SCROLL_POSITION_KEY);
    if (!saved) return null;

    const parts = saved.split(':');
    if (parts.length !== 3) return null;

    const book = parts[0] as BibleBook;
    const chapter = parseInt(parts[1], 10);
    const verse = parseInt(parts[2], 10);

    if (isNaN(chapter) || isNaN(verse)) return null;

    return { book, chapter, verse };
}

/**
 * Find the paragraph index that contains a specific verse
 * Uses the startPositions index for O(1) chapter lookup,
 * then searches within the chapter for the verse (O(k) where k = paragraphs in chapter)
 */
export function findParagraphIndexForVerse(
    paragraphs: TranslationParagraph[],
    startPositions: TranslationByParagraph['startPositions'],
    book: BibleBook,
    chapter: number,
    verse: number
): number {
    // Step 1: Get starting paragraph for this chapter (O(1))
    const chapterStartIndex = startPositions[book]?.[chapter];
    if (chapterStartIndex === undefined) {
        // Fallback: If we can't find the chapter, but startPositions exists, try to find the book
        if (startPositions[book]) {
            const firstAvailableChapter = Object.keys(startPositions[book])[0];
            if (firstAvailableChapter !== undefined) {
                console.warn(`Chapter ${chapter} not found in ${book}, using first available chapter ${firstAvailableChapter}`);
                return startPositions[book][firstAvailableChapter as any];
            }
        }
        console.warn(`No start position found for ${book} ${chapter}`);
        return 0;
    }

    // Step 2: Find the end of this chapter by looking for the next chapter start
    let nextChapterIndex = paragraphs.length; // Default to end of Bible

    // First try next chapter in same book
    if (startPositions[book]?.[chapter + 1] !== undefined) {
        nextChapterIndex = startPositions[book][chapter + 1];
    } else {
        // We're at the end of this book - find the start of the next book
        // Use BIBLE_BOOKS array to find the next book in canonical order (O(1) lookup)
        const currentBookIndex = BIBLE_BOOKS.findIndex(b => b.name === book);
        if (currentBookIndex !== -1 && currentBookIndex < BIBLE_BOOKS.length - 1) {
            // Get the next book in canonical order
            const nextBook = BIBLE_BOOKS[currentBookIndex + 1].name;
            // Get the first chapter of the next book
            const nextBookFirstChapter = startPositions[nextBook]?.[1];
            if (nextBookFirstChapter !== undefined) {
                nextChapterIndex = nextBookFirstChapter;
            }
        }
        // If we're at Revelation (last book), nextChapterIndex stays as paragraphs.length
    }

    // Step 3: Search within chapter for verse (O(k) where k = paragraphs in chapter)
    for (let i = chapterStartIndex; i < nextChapterIndex; i++) {
        const paragraph = paragraphs[i];
        if (!paragraph) continue;

        // Parse reference: "Gen:1:3:1:6" = Genesis 1:3 - 1:6
        const parts = paragraph.r.split(':');
        if (parts.length !== 5) continue;

        const verseStart = parseInt(parts[2], 10);
        const verseEnd = parseInt(parts[4], 10);

        // Check if this paragraph contains the verse
        if (verse >= verseStart && verse <= verseEnd) {
            return i;
        }
    }

    // Fallback: return chapter start if verse not found
    console.warn(`Verse ${book} ${chapter}:${verse} not found, using chapter start`);
    return chapterStartIndex;
}

/**
 * Calculate the initial scroll index from URL parameters.
 * Priority 1: verse query param
 * Priority 2: Calculate from book/chapter path params (start of chapter)
 */
export function getInitialScrollIndex(
    paragraphsData: ItemData<TranslationByParagraph> | undefined,
    params: { book?: string; chapter?: string } | null,
    verseParam?: string
): number {
    if (!paragraphsData || !params?.book || !paragraphsData.items || paragraphsData.items.length === 0) return 0;

    const translation = paragraphsData.items[0];
    if (!translation || !translation.paragraphs || !translation.startPositions) return 0;

    const book = params.book as BibleBook;
    const chapter = parseInt(params.chapter || '1', 10);

    // Priority 1: verse query param
    if (verseParam) {
        const verse = parseInt(verseParam, 10);
        if (!isNaN(verse)) {
            return findParagraphIndexForVerse(
                translation.paragraphs,
                translation.startPositions,
                book,
                chapter,
                verse
            );
        }
    }

    // Priority 2: Calculate from book/chapter path params (start of chapter)
    const chapterStartIndex = translation.startPositions[book]?.[chapter];
    if (chapterStartIndex !== undefined) {
        return chapterStartIndex;
    }

    return 0;
}

/**
 * Get the redirect URL for /app/read when no book/chapter params are present.
 * Priority 1: verse from localStorage
 * Priority 2: Default to Genesis 1
 */
export function getReadingRedirectUrl(): string {
    // Priority 1: Check localStorage for saved verse
    const savedPosition = loadScrollPosition();
    if (savedPosition) {
        return `/app/read/${savedPosition.book}/${savedPosition.chapter}?verse=${savedPosition.verse}`;
    }

    // Priority 2: No saved position, go to Genesis 1
    return '/app/read/Genesis/1';
}

/**
 * Extract verse number from a paragraph reference
 * Reference format: "Gen:1:3:1:6" (book:chapterStart:verseStart:chapterEnd:verseEnd)
 * Returns the starting verse number
 */
export function getVerseFromParagraphRef(paragraphRef: string): number {
    const parts = paragraphRef.split(':');
    if (parts.length === 5) {
        return parseInt(parts[2], 10); // verseStart
    }
    return 1; // Default to verse 1 if parsing fails
}



